/**
 * Wiadomosc - pojedyncza banka w rozmowie.
 *
 * Odpowiada za: naglowek (autor, godzina), tresc, zalaczniki (miniatura obrazu
 * albo kafelek pliku) i usuniecie WLASNEJ wiadomosci.
 *
 * Wiadomosc usunieta przychodzi z backendu jako tresc=null + usunieta=true -
 * renderujemy kursywa "Wiadomość usunięta". Tresc zostaje w bazie (widzi ja
 * administrator), ale nigdy nie opuszcza serwera przez zwykle API.
 */

import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { zalacznikBlobUrl, pobierzZalacznik, usunWiadomosc } from '../../services/komunikatorService';

// ---------------------------------------------------------------------------
// Pomocnicze formatowanie (uzywane takze przez OknoRozmowy i ListaRozmow)
// ---------------------------------------------------------------------------

/**
 * Backend zwraca daty jako "RRRR-MM-DD GG:MM:SS" (isoformat ze spacja).
 * Safari nie parsuje wariantu ze spacja - stad podmiana na 'T'.
 */
export const parsujDate = (wartosc) => {
  if (!wartosc) return null;
  if (wartosc instanceof Date) return wartosc;
  const d = new Date(String(wartosc).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatujGodzine = (wartosc) => {
  const d = parsujDate(wartosc);
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** "Dzisiaj" / "Wczoraj" / "12.08.2026" - separator dnia w rozmowie. */
export const etykietaDnia = (wartosc) => {
  const d = parsujDate(wartosc);
  if (!d) return '';
  const dzis = new Date();
  const bezCzasu = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const roznica = Math.round((bezCzasu(dzis) - bezCzasu(d)) / 86400000);
  if (roznica === 0) return 'Dzisiaj';
  if (roznica === 1) return 'Wczoraj';
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Krotki czas na liscie rozmow: godzina dzisiaj, inaczej data. */
export const skrotCzasu = (wartosc) => {
  const d = parsujDate(wartosc);
  if (!d) return '';
  const dzis = new Date();
  const tenSamDzien = d.getDate() === dzis.getDate()
    && d.getMonth() === dzis.getMonth()
    && d.getFullYear() === dzis.getFullYear();
  if (tenSamDzien) return formatujGodzine(d);
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
};

export const formatujRozmiar = (bajty) => {
  const b = Number(bajty);
  if (!b || b < 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const czyObraz = (zalacznik) => {
  const mime = (zalacznik?.mime_type || '').toLowerCase();
  return mime.startsWith('image/');
};

const IKONY_PLIKOW = [
  [/pdf/, '📕'],
  [/word|document/, '📘'],
  [/sheet|excel|csv/, '📗'],
  [/zip|compressed/, '🗜️'],
  [/text/, '📄'],
];

const ikonaPliku = (mime) => {
  const m = (mime || '').toLowerCase();
  const trafienie = IKONY_PLIKOW.find(([wzorzec]) => wzorzec.test(m));
  return trafienie ? trafienie[1] : '📎';
};

// ---------------------------------------------------------------------------
// Miniatura obrazu - laduje blob przez axios (naglowki auth), nie przez goly src
// ---------------------------------------------------------------------------

const MiniaturaObrazu = ({ zalacznik, onPodglad }) => {
  const [url, setUrl] = useState(null);
  const [blad, setBlad] = useState(false);
  const zywyRef = useRef(true);

  useEffect(() => {
    zywyRef.current = true;
    setUrl(null);
    setBlad(false);
    // Promise.resolve(...) - gdyby serwis kiedykolwiek zwrocil cos innego niz
    // obietnice, banka ma pokazac blad, a nie wywalic calego widgetu.
    Promise.resolve(zalacznikBlobUrl(zalacznik.id, !!zalacznik.ma_miniature))
      .then((u) => { if (zywyRef.current) setUrl(u); })
      .catch(() => { if (zywyRef.current) setBlad(true); });
    return () => { zywyRef.current = false; };
  }, [zalacznik.id, zalacznik.ma_miniature]);

  // Proporcje z metadanych - dzieki temu banka nie "skacze" po zaladowaniu.
  const proporcje = (zalacznik.szerokosc && zalacznik.wysokosc)
    ? Math.min(1.8, Math.max(0.5, zalacznik.szerokosc / zalacznik.wysokosc))
    : 1.35;
  const szerokosc = 210;
  const wysokosc = Math.round(szerokosc / proporcje);

  if (blad) {
    return (
      <button
        type="button"
        className="kom-plik"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
        onClick={() => pobierzZalacznik(zalacznik.id, zalacznik.nazwa).catch((e) => toast.error(e.message))}
      >
        <span className="kom-plik-ikona">🖼️</span>
        <span className="kom-plik-opis">
          <span className="kom-plik-nazwa">{zalacznik.nazwa}</span>
          <span className="kom-plik-meta">Podgląd niedostępny — kliknij, aby pobrać</span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="kom-obraz"
      style={{ background: 'var(--bg-secondary)', border: 'none', width: szerokosc, height: wysokosc }}
      onClick={() => onPodglad && onPodglad(zalacznik)}
      title={`${zalacznik.nazwa || 'Zdjęcie'} — kliknij, aby powiększyć`}
    >
      {url
        ? <img src={url} alt={zalacznik.nazwa || 'Zdjęcie'} loading="lazy" />
        : <span className="kom-obraz-ladowanie">Ładowanie…</span>}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Kafelek pliku nie-obrazkowego
// ---------------------------------------------------------------------------

const KafelekPliku = ({ zalacznik }) => {
  const [pobieranie, setPobieranie] = useState(false);

  const pobierz = async () => {
    if (pobieranie) return;
    setPobieranie(true);
    try {
      await pobierzZalacznik(zalacznik.id, zalacznik.nazwa || 'zalacznik');
    } catch (e) {
      toast.error(e.message || 'Nie udało się pobrać pliku');
    } finally {
      setPobieranie(false);
    }
  };

  return (
    <button
      type="button"
      className="kom-plik"
      style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
      onClick={pobierz}
      title="Kliknij, aby pobrać"
    >
      <span className="kom-plik-ikona">{ikonaPliku(zalacznik.mime_type)}</span>
      <span className="kom-plik-opis">
        <span className="kom-plik-nazwa">{zalacznik.nazwa || 'Załącznik'}</span>
        <span className="kom-plik-meta">
          {pobieranie ? 'Pobieranie…' : formatujRozmiar(zalacznik.rozmiar_bajty)}
        </span>
      </span>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Wiadomosc
// ---------------------------------------------------------------------------

const Wiadomosc = ({ wiadomosc, pokazNaglowek, onPodglad, onUsunieta }) => {
  const [usuwanie, setUsuwanie] = useState(false);
  const wlasna = !!wiadomosc.wlasna;
  const usunieta = !!wiadomosc.usunieta;
  const systemowa = wiadomosc.typ === 'system';

  const usun = async () => {
    if (usuwanie) return;
    const potwierdzone = window.confirm(
      'Usunąć tę wiadomość? Pozostali zobaczą „Wiadomość usunięta”.'
    );
    if (!potwierdzone) return;
    setUsuwanie(true);
    try {
      await usunWiadomosc(wiadomosc.id);
      if (onUsunieta) onUsunieta(wiadomosc.id);
    } catch (e) {
      toast.error(e.message || 'Nie udało się usunąć wiadomości');
    } finally {
      setUsuwanie(false);
    }
  };

  if (systemowa) {
    return (
      <div className="kom-wiadomosc-system">
        {wiadomosc.tresc}
        <span className="kom-godzina"> {formatujGodzine(wiadomosc.utworzono)}</span>
      </div>
    );
  }

  const zalaczniki = Array.isArray(wiadomosc.zalaczniki) ? wiadomosc.zalaczniki : [];

  return (
    <div className={`kom-wiersz ${wlasna ? 'kom-wiersz-wlasna' : ''} ${pokazNaglowek ? '' : 'kom-wiersz-ciag'}`}>
      <div className="kom-banka-kontener">
        {pokazNaglowek && !wlasna && (
          <div className="kom-autor">{wiadomosc.autor_nazwa || wiadomosc.autor_login}</div>
        )}

        <div
          className="kom-banka"
          style={{
            background: wlasna ? 'var(--primary-light)' : 'var(--card-bg)',
            borderColor: wlasna ? 'var(--primary)' : 'var(--border)',
          }}
        >
          {usunieta ? (
            <div className="kom-usunieta">Wiadomość usunięta</div>
          ) : (
            <>
              {zalaczniki.length > 0 && (
                <div className="kom-zalaczniki">
                  {zalaczniki.map((z) => (czyObraz(z)
                    ? <MiniaturaObrazu key={z.id} zalacznik={z} onPodglad={onPodglad} />
                    : <KafelekPliku key={z.id} zalacznik={z} />
                  ))}
                </div>
              )}
              {wiadomosc.tresc ? <div className="kom-tresc-wiadomosci">{wiadomosc.tresc}</div> : null}
            </>
          )}

          <div className="kom-stopka">
            <span className="kom-godzina">{formatujGodzine(wiadomosc.utworzono)}</span>
            {wlasna && !usunieta && (
              <button
                type="button"
                className="kom-usun"
                style={{ background: 'transparent', border: 'none' }}
                onClick={usun}
                disabled={usuwanie}
                title="Usuń wiadomość"
                aria-label="Usuń wiadomość"
              >
                {usuwanie ? '…' : '🗑'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wiadomosc;
